/**
 * GroupBySelector
 *
 * Dynamic dropdown for selecting the group-by field in report view.
 * Options are fetched from Frappe meta - only shows groupable fieldtypes.
 */

import { useReportMeta } from "../../api/useReportMeta";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Layers } from "lucide-react";

interface GroupBySelectorProps {
  doctype: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}

export function GroupBySelector({
  doctype,
  value,
  onChange,
  disabled,
}: GroupBySelectorProps) {
  const { data: meta, isLoading } = useReportMeta(doctype);

  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger className="w-48 h-10">
          <SelectValue placeholder="Loading..." />
        </SelectTrigger>
      </Select>
    );
  }

  const fields = meta?.groupable_fields ?? [];

  if (fields.length === 0) {
    return null; // No groupable fields for this doctype
  }

  return (
    <div className="flex items-center gap-2">
      <Layers className="h-4 w-4 text-muted-foreground" />
      <Select
        value={value ?? "__none__"}
        onValueChange={(v) => onChange(v === "__none__" ? undefined : v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-48 h-10">
          <SelectValue placeholder="Group by..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">No grouping</SelectItem>
          {fields.map((field) => (
            <SelectItem key={field.fieldname} value={field.fieldname}>
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
